import {
	ApproveRequest,
	EventInstanceItem,
	PendingReviewItem,
	RejectRequest,
	ReviewDetail,
} from "@/domain/eventInstance.interface";
import { instanceBo } from "../utils/axios.create";

// instanceBo baseURL bakes in `/accounts/{ACCOUNT_ID}`, so paths here are
// relative to that — no `/accounts/{id}` prefix here.

const listPendingReview = async (): Promise<PendingReviewItem[]> => {
	const res = await instanceBo.get(
		"/enrollments/event-instances/pending-review",
	);
	return res.data?.body ?? [];
};

const listEventInstancesForEnrollment = async (
	enrollmentId: string,
	entryMethod?: "Manual" | "Automatic",
): Promise<EventInstanceItem[]> => {
	const res = await instanceBo.get(
		`/enrollments/${enrollmentId}/event-instances`,
		{ params: entryMethod ? { entryMethod } : undefined },
	);
	return res.data?.body ?? [];
};

const getReviewDetail = async (
	enrollmentId: string,
	instanceId: string,
): Promise<ReviewDetail> => {
	const res = await instanceBo.get(
		`/enrollments/${enrollmentId}/event-instances/${instanceId}/review-detail`,
	);
	return res.data?.body;
};

/**
 * SIMS-8319 AC2 — a short-lived signed playback URL for a captured PHI video.
 *
 * Served by SIMS-8306, which checks that THIS reviewer is entitled to THIS submission and
 * audit-logs the decision before issuing anything. Call it again whenever the link expires:
 * expiry is a designed outcome (screen 3f), each call mints a fresh URL, and nothing is cached.
 */
const getPrivateMediaPlaybackUrl = async (
	enrollmentId: string,
	instanceId: string,
	userId: string,
): Promise<{ url: string; expiresInSeconds: number }> => {
	const res = await instanceBo.get(
		`/enrollments/${enrollmentId}/event-instances/${instanceId}/private-media/playback-url`,
		{ params: { userId } },
	);
	return res.data?.body;
};

const approveEventInstance = async (
	enrollmentId: string,
	instanceId: string,
	reviewerId: string,
	body: ApproveRequest,
): Promise<void> => {
	await instanceBo.put(
		`/enrollments/${enrollmentId}/event-instances/${instanceId}/approve`,
		body,
		{ params: { userId: reviewerId } },
	);
};

const rejectEventInstance = async (
	enrollmentId: string,
	instanceId: string,
	reviewerId: string,
	body: RejectRequest,
): Promise<void> => {
	await instanceBo.put(
		`/enrollments/${enrollmentId}/event-instances/${instanceId}/reject`,
		body,
		{ params: { userId: reviewerId } },
	);
};

export const eventInstanceService = {
	listPendingReview,
	listEventInstancesForEnrollment,
	getReviewDetail,
	getPrivateMediaPlaybackUrl,
	approveEventInstance,
	rejectEventInstance,
};
